import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, AbstractControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Actions, ofType } from '@ngrx/effects';
import { ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { userRecordSelector, userRecordNameSelector } from 'src/app/user/store/user.selectors';
import { tap, filter, takeUntil, map, switchMap, catchError, take } from 'rxjs/operators';
import { UpdateUserRecordAction, UpdateUserRecordErrorAction, SetUserRecordAction, FetchUserRecordAction } from 'src/app/user/store/user.actions';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { TranslateService } from '@ngx-translate/core';
import { emailRegex } from 'src/app/global/validators/match.validator';
import { ProfileField } from '@bciti/types';
import { formatDateOnly, parseDateForDisplay } from 'src/app/global/utils/date/date.utils';
import { profileOptionsSelector, organisationConfigurationSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { MatchEmailValidator } from 'src/app/global/validators/matchEmail.validator';
import { ResidenceService } from 'src/app/residence/service/residence.abstract.service';
import { Residence, ResidenceSubType, ProfileAddress, OrgLink, getResidenceDisplayState, needsReview as residenceNeedsReview } from 'src/app/residence/residence.model';
import { GeoidAddressModalComponent } from 'src/app/global/components/forms/geoid-address/modal/geoid-address-modal.component';
import { ReviewResidenceDialogComponent } from 'src/app/residence/dialogs/review-residence-dialog/review-residence-dialog.component';
import { VerifyAddressDialogComponent } from 'src/app/residence/dialogs/verify-address-dialog/verify-address-dialog.component';
import { RenameResidenceDialogComponent } from 'src/app/residence/dialogs/rename-residence-dialog/rename-residence-dialog.component';
import { ChangeSubTypeDialogComponent } from 'src/app/residence/dialogs/change-subtype-dialog/change-subtype-dialog.component';
import { ConfirmDialogComponent } from 'src/app/global/components/dialogs/confirm-dialog/confirm-dialog.component';
import { UserService } from '../../service/user.abstract.service';
import { Address, GetFamilyLinksItem, UpdateUserRecordPayload, UserRecord } from '../../user.model';
import { UserUtilService } from '../../util/user-util.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    update$ = new ReplaySubject();

    private userRecord: UserRecord;

    public userid: string;

    public name$ = this.store.select(userRecordNameSelector);

    public profileForm: FormGroup;

    public loading = false;

    public profileLoaded = false;

    private dummyAccount = false;

    public t: any = false;

    public maxDate = new Date();

    public orgConfig;

    public hiddenFields: ProfileField[] = [];

    public profileField = ProfileField;

    public changeEmail = false;

    public originalEmail: string;

    private pendingEmailChange: { oldEmail: string | null; newEmail: string | null } | null = null;

    public activeLinks: GetFamilyLinksItem[] = [];

    public activeFamilyEmail = false;

    public profileAddresses: ProfileAddress[] = [];

    public orgTag: string;

    private residencesMap = new Map<string, Residence>();

    constructor(
        private route: ActivatedRoute,
        private formBuilder: FormBuilder,
        private store: Store<State>,
        private actions$: Actions,
        private messageService: MessageService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
        private userService: UserService,
        private residenceService: ResidenceService,
        private dialog: MatDialog,
        private userUtilService: UserUtilService,
    ) {}

    get firstName(): AbstractControl {
        return this.profileForm?.get('firstName');
    }

    get lastName(): AbstractControl {
        return this.profileForm?.get('lastName');
    }

    get sex(): AbstractControl {
        return this.profileForm?.get('sex');
    }

    get profilePicture(): AbstractControl {
        return this.profileForm?.get('profilePicture');
    }

    get phone(): AbstractControl {
        return this.profileForm?.get('phone');
    }

    get picture(): AbstractControl {
        return this.profileForm?.get('picture');
    }

    get secPhone(): AbstractControl {
        return this.profileForm?.get('secPhone');
    }

    get secPhoneExt(): AbstractControl {
        return this.profileForm?.get('secPhoneExt');
    }

    get cellPhone(): AbstractControl {
        return this.profileForm?.get('cellPhone');
    }

    get birthDate(): AbstractControl {
        return this.profileForm?.get('birthDate');
    }

    get mainEmail(): AbstractControl {
        return this.profileForm?.get('mainEmail');
    }

    get email(): AbstractControl {
        return this.profileForm?.get('email');
    }

    get confirmEmail(): AbstractControl {
        return this.profileForm?.get('confirmEmail');
    }

    get emailBeingCleared(): boolean {
        return this.changeEmail && !this.email?.value && !!this.originalEmail;
    }

    get address(): AbstractControl {
        return this.profileForm?.get('address');
    }

    public validateDateIsInPast(c: FormControl): { [key: string]: { [key: string]: boolean } } | null {
        if (c.value) {
            const today = new Date().toISOString();
            return new Date(c.value).toISOString() < today ? null : { validateDateIsInPast: { valid: false } };
        }
        return null;
    }

    public ngOnInit(): void {
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            this.userid = params.get('id');
            if (this.userid) {
                this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
            }
        });

        this.translateService
            .get('pages.user-update')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));

        combineLatest([
            this.store.select(profileOptionsSelector),
            this.store.select(userRecordSelector),
            this.store.select(organisationConfigurationSelector),
            this.store.select(organisationTagSelector),
        ])
            .pipe(
                filter(([_, userRecordValues]) => !!userRecordValues && userRecordValues.id === this.userid),
                takeUntil(this.destroy$),
                tap(() => (this.profileLoaded = false)),
                map(([profileOptions, userRecordValues, orgConfig, orgTag]) => {
                    this.orgTag = orgTag;
                    // Only recreate form if this is the first load or if the user record ID changed
                    const isNewUser = !this.userRecord || this.userRecord.id !== userRecordValues.id;

                    if (isNewUser) {
                        this.hiddenFields = profileOptions?.hiddenFields ?? [];
                        this.initializeProfileForm();
                        this.profileForm.reset();
                        if (this.hiddenFields) {
                            this.removeHiddenFields();
                        }
                        this.orgConfig = orgConfig;
                        if (orgConfig?.dummyAccount) {
                            this.dummyAccount = true;
                            this.profileForm.controls.secEmail?.clearValidators();
                            this.profileForm.controls.email?.setValidators(Validators.pattern(emailRegex));
                            this.profileForm.controls.secEmail?.updateValueAndValidity();
                        }
                    }

                    // Always update the form with latest user data
                    this.populateProfileForm(userRecordValues);
                    this.cdr.detectChanges();
                }),
                tap(() => (this.profileLoaded = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateUserRecordAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        // Listen for SetUserRecordAction after UpdateUserRecordAction to detect success
        this.actions$
            .pipe(
                ofType(UpdateUserRecordAction),
                switchMap(() =>
                    this.actions$.pipe(
                        ofType(SetUserRecordAction),
                        take(1),
                        tap(() => {
                            if (this.pendingEmailChange) {
                                this.syncEmailWithZitadel(this.pendingEmailChange.newEmail);
                            } else {
                                this.loading = false;
                                this.profileForm.markAsUntouched();
                                this.profileForm.markAsPristine();
                                setTimeout(() => {
                                    this.messageService.success(this?.t?.updateUserRecordSuccess);
                                }, 0);
                            }
                        }),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateUserRecordErrorAction),
                tap(() => (this.loading = false)),
                tap(({ error }) => {
                    if (error?.status === 409) {
                        [this.email, this.confirmEmail].forEach((c) => {
                            c?.setErrors({ emailAlreadyUsed: true });
                            c?.markAsTouched();
                        });
                    }
                }),
            )
            .subscribe();

        this.update$
            .pipe(
                tap(() => (this.loading = true)),
                switchMap(() => this.route.paramMap),
                map((params) => params.get('id')),
                switchMap((id) =>
                    this.userService.getUserFamilyLinks(id).pipe(
                        takeUntil(this.destroy$),
                        catchError((error) => {
                            this.messageService.error(error.messages?.[0] || this.translateService.instant('pages.userFamily.error.common'));
                            return of([]);
                        }),
                        tap((links) => (this.activeLinks = links.filter((l) => l.cityApproved && l.familyApproved))),
                        tap(() => (this.loading = false)),
                    ),
                ),
            )
            .subscribe();
    }

    initializeProfileForm() {
        this.profileForm = this.formBuilder.group(
            {
                firstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
                lastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
                sex: new FormControl('', []),
                birthDate: new FormControl('', [this.validateDateIsInPast]),
                address: new FormControl(''),
                picture: new FormControl(''),
                phone: new FormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
                secPhone: new FormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
                secPhoneExt: new FormControl({ value: '', disabled: true }),
                cellPhone: new FormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
                email: new FormControl('', []),
                confirmEmail: new FormControl('', []),
            },

            {
                validators: [MatchEmailValidator('email', 'confirmEmail', () => this.changeEmail && !this.emailBeingCleared)],
                updateOn: 'change',
            },
        );

        this.profileForm
            .get('secPhone')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const secPhoneControl = this.profileForm.get('secPhone');
                const secPhoneExtControl = this.profileForm.get('secPhoneExt');

                if (secPhoneControl.valid && secPhoneControl.value) {
                    secPhoneExtControl.enable();
                } else {
                    secPhoneExtControl.disable();
                    secPhoneExtControl.setValue('');
                }
            });
        this.email?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.updateConfirmEmailValidators(value);
        });
    }

    removeHiddenFields() {
        const controls = Object.keys(this.profileForm.controls);
        controls.forEach((controlName) => {
            this.hiddenFields.forEach((hiddenField) => {
                if (controlName.toUpperCase() === (hiddenField as string) && this.profileForm.get(controlName)) {
                    this.profileForm.removeControl(controlName);
                }
            });
        });
        this.profileForm.updateValueAndValidity();
    }

    displayField(hiddenField: ProfileField): boolean {
        return !this.hiddenFields.includes(hiddenField);
    }

    populateProfileForm(userRecord: UserRecord) {
        this.userRecord = userRecord;
        this.originalEmail = userRecord?.email || '';

        // Skip overwriting the form fields if the user has unsaved edits in progress (e.g. mid-edit
        // while adding an address triggers a background refetch) - only the Addresses section below
        // should reflect the latest server state in that case.
        if (!this.profileForm.dirty) {
            this.firstName?.setValue(userRecord.firstName || '');
            this.lastName?.setValue(userRecord.lastName || '');
            this.birthDate?.setValue(parseDateForDisplay(userRecord.birthDate) || '');
            this.phone?.setValue(userRecord.phone || '');
            this.sex?.setValue(userRecord.sex || '');
            this.secPhone?.setValue(userRecord.secPhone || '');
            this.secPhoneExt?.setValue(userRecord.secPhoneExt || '');
            this.cellPhone?.setValue(userRecord.cellPhone || '');
            this.email?.setValue(userRecord?.email || '');
            this.picture?.setValue(userRecord.picture || '');

            if (userRecord.addresses && userRecord.addresses[0]) {
                this.address?.setValue(userRecord.addresses[0]);
            } else {
                this.address?.setValue('');
            }
            this.profileForm.markAsPristine();
            this.profileForm.markAsUntouched();
        }

        this.loadResidencesForUser(userRecord);
    }

    private loadResidencesForUser(userRecord: UserRecord): void {
        if (!userRecord?.id) return;

        this.residenceService
            .getCitizenResidences(userRecord.id)
            .pipe(
                take(1),
                catchError(() => of([])),
            )
            .subscribe((residences) => {
                this.residencesMap.clear();
                residences.forEach((r) => this.residencesMap.set(r._id, r));
                this.profileAddresses = this.mergeAddresses(userRecord.addresses || [], residences);
                this.cdr.detectChanges();
            });
    }

    private mergeAddresses(userAddresses: Address[], residences: Residence[]): ProfileAddress[] {
        const matched = new Set<string>();

        return userAddresses.map((addr) => {
            const addrStreet = this.normalizeString(addr.streetName || addr.street);
            const addrPostCode = this.normalizeString(addr.postCode);
            const addrNumber = String(addr.number || '');
            const addrUnit = this.normalizeString(addr.unit || '');

            const matchingResidences = residences.filter(
                (r) =>
                    !matched.has(r._id) &&
                    this.normalizeString(r.address.streetName || r.address.street) === addrStreet &&
                    String(r.address.number || '') === addrNumber &&
                    this.normalizeString(r.address.postCode) === addrPostCode &&
                    this.normalizeString(r.address.unit || '') === addrUnit,
            );

            matchingResidences.forEach((r) => matched.add(r._id));

            const orgLinks: OrgLink[] = matchingResidences.map((r) => ({
                tag: r.organisation,
                name: r.organisation,
                isPrimary: r.isPrimary,
                verificationStatus: r.verification.status,
                residenceId: r._id,
                displayState: getResidenceDisplayState(r),
                needsReview: residenceNeedsReview(r),
                expirationDate: r.expirationDate,
                subType: r.subType,
            }));

            return {
                address: addr,
                displayName: matchingResidences[0]?.name ?? (addr as any).name ?? `${addr.streetName}, ${addr.city}`,
                orgLinks,
                isCasual: orgLinks.length === 0,
                isVerified: orgLinks.some((link) => link.verificationStatus === 'APPROVED'),
            };
        });
    }

    private normalizeString(str: string): string {
        return (str || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '')
            .toLowerCase();
    }

    public setMainAddress(pa: ProfileAddress): void {
        const addressId = (pa.address as any)._id || (pa.address as any).id;
        if (!addressId || !this.userid) return;

        this.userService
            .setMainAddress(this.userid, addressId)
            .pipe(
                take(1),
                catchError(() => {
                    this.messageService.error(this.translateService.instant('pages.residences.toast.error'));
                    return of(null);
                }),
            )
            .subscribe((result) => {
                if (result) {
                    this.messageService.success(this.translateService.instant('pages.profile.addresses.setAsPrimarySuccess'));
                    this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
                }
            });
    }

    public openChangeSubTypeDialog(pa: ProfileAddress): void {
        const residenceId = pa.orgLinks[0]?.residenceId;
        if (!residenceId) return;

        this.dialog
            .open(ChangeSubTypeDialogComponent, {
                width: '420px',
                data: { currentSubType: pa.orgLinks[0].subType },
            })
            .afterClosed()
            .pipe(
                filter((newSubType) => !!newSubType && newSubType !== pa.orgLinks[0]?.subType),
                switchMap((newSubType) =>
                    this.residenceService.updateResidenceSubType(residenceId, newSubType).pipe(
                        catchError((error) => {
                            this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                            return of(null);
                        }),
                    ),
                ),
                filter((result) => !!result),
                tap(() => {
                    this.messageService.success(this.translateService.instant('pages.profile.addresses.changeSubTypeSuccess'));
                    this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
                }),
            )
            .subscribe();
    }

    public editResidenceAddress(pa: ProfileAddress): void {
        const residenceId = pa.orgLinks[0]?.residenceId;
        if (!residenceId) return;

        this.dialog
            .open(GeoidAddressModalComponent, {
                data: {
                    title: this.translateService.instant('pages.profile.addresses.editAddressTitle'),
                    streetName: `${pa.address.number || ''} ${pa.address.streetName || pa.address.street}`.trim(),
                    city: pa.address.city,
                    postalCode: pa.address.postCode,
                    unit: pa.address.unit,
                },
            })
            .afterClosed()
            .pipe(
                filter((newAddress) => !!newAddress),
                switchMap((newAddress) =>
                    this.residenceService.updateResidenceAddress(residenceId, newAddress).pipe(
                        catchError((error) => {
                            this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                            return of(null);
                        }),
                    ),
                ),
                filter((result) => !!result),
                tap(() => {
                    this.messageService.success(this.translateService.instant('pages.profile.addresses.editAddressSuccess'));
                    this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
                }),
            )
            .subscribe();
    }

    public renameResidence(pa: ProfileAddress): void {
        const residenceId = pa.orgLinks[0]?.residenceId;
        if (!residenceId) return;

        this.dialog
            .open(RenameResidenceDialogComponent, {
                width: '420px',
                data: { currentName: pa.displayName },
            })
            .afterClosed()
            .pipe(
                filter((newName) => !!newName && newName !== pa.displayName),
                switchMap((newName) =>
                    this.residenceService.updateResidenceName(residenceId, newName).pipe(
                        catchError((error) => {
                            this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                            return of(null);
                        }),
                    ),
                ),
                filter((result) => !!result),
                tap(() => {
                    this.messageService.success(this.translateService.instant('pages.profile.addresses.renameSuccess'));
                    this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
                }),
            )
            .subscribe();
    }

    public reviewResidence(pa: ProfileAddress): void {
        const residenceId = pa.orgLinks[0]?.residenceId;
        if (!residenceId) return;

        const residence = this.residencesMap.get(residenceId);
        if (!residence) return;

        this.dialog
            .open(ReviewResidenceDialogComponent, {
                width: '560px',
                data: {
                    residence,
                    citizen: {
                        id: this.userid,
                        firstName: this.userRecord?.firstName,
                        lastName: this.userRecord?.lastName,
                        email: this.userRecord?.email,
                        phone: this.userRecord?.phone,
                        picture: this.userRecord?.picture,
                    },
                },
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                tap(() => this.store.dispatch(FetchUserRecordAction({ userid: this.userid }))),
            )
            .subscribe();
    }

    public verifyAddress(pa: ProfileAddress): void {
        if (!this.userid) return;

        this.dialog
            .open(VerifyAddressDialogComponent, {
                width: '480px',
                data: {
                    address: pa.address,
                    displayName: pa.displayName,
                },
            })
            .afterClosed()
            .pipe(
                filter((selectedSubType) => !!selectedSubType),
                switchMap((selectedSubType) =>
                    this.store.select(organisationTagSelector).pipe(
                        take(1),
                        map((orgTag) => ({ orgTag, selectedSubType })),
                    ),
                ),
                switchMap(({ orgTag, selectedSubType }) =>
                    this.residenceService
                        .createResidence({
                            userId: this.userid,
                            address: pa.address,
                            organisation: orgTag,
                            subType: selectedSubType,
                        })
                        .pipe(
                            catchError((error) => {
                                this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                                return of(null);
                            }),
                        ),
                ),
                filter((result) => !!result),
                tap(() => {
                    this.messageService.success(this.translateService.instant('pages.profile.addresses.verifySuccess'));
                    this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
                }),
            )
            .subscribe();
    }

    public deleteAddress(pa: ProfileAddress): void {
        if (!this.userid) return;

        const addressId = (pa.address as any)._id || (pa.address as any).id;
        if (!addressId) {
            this.messageService.error(this.translateService.instant('pages.residences.toast.error'));
            return;
        }

        const confirmMessage = this.translateService.instant('pages.profile.addresses.deleteConfirm', { name: pa.displayName });

        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
            width: '500px',
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translateService.instant('pages.profile.addresses.delete'),
                body: confirmMessage,
                buttons: {
                    confirm: {
                        text: this.translateService.instant('pages.profile.addresses.delete'),
                        class: 'bciti-button bciti-button--red',
                    },
                    cancel: {
                        text: this.translateService.instant('pages.residences.reviewDialog.cancelButton'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        confirmDialog.componentInstance.onSubmit.subscribe((confirmed) => {
            confirmDialog.componentInstance.dialogRef.close();
            if (confirmed) {
                this.userService
                    .deleteUserAddress(this.userid, addressId)
                    .pipe(
                        take(1),
                        catchError((error) => {
                            this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                            return of(null);
                        }),
                    )
                    .subscribe((result) => {
                        if (result !== null) {
                            this.messageService.success(this.translateService.instant('pages.profile.addresses.deleteSuccess'));
                            this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
                        }
                    });
            }
        });
    }

    public openAddAddressDialog(): void {
        if (!this.userid) return;

        this.dialog
            .open(GeoidAddressModalComponent, { data: { showSubTypeSelector: true } })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                switchMap((result) =>
                    this.store.select(organisationTagSelector).pipe(
                        take(1),
                        map((orgTag) => ({ address: result.address, subType: result.subType, orgTag })),
                    ),
                ),
                switchMap(({ address, subType, orgTag }) =>
                    this.residenceService
                        .createResidence({
                            userId: this.userid,
                            address,
                            organisation: orgTag,
                            subType: subType || 'RESIDENT',
                        })
                        .pipe(
                            catchError((error) => {
                                this.messageService.error(this.userUtilService.resolveApiErrorMessage(error, 'pages.residences.toast.error'));
                                return of(null);
                            }),
                        ),
                ),
                filter((result) => !!result),
                tap(() => {
                    this.messageService.success(this.translateService.instant('pages.profile.addresses.addSuccess'));
                    this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
                }),
            )
            .subscribe();
    }

    public save(): void {
        this.profileForm.markAllAsTouched();

        if (this.profileForm.valid) {
            const payload: UpdateUserRecordPayload = {};

            if (this?.firstName?.value !== '') {
                payload.firstName = this?.firstName?.value;
            }
            if (this?.lastName?.value !== '') {
                payload.lastName = this?.lastName?.value;
            }
            if (this?.sex?.value !== '') {
                payload.sex = this?.sex?.value;
            }
            if (this?.phone?.value !== '') {
                payload.phone = this?.phone?.value;
            }

            if (this?.secPhone?.value !== '') {
                payload.secPhone = this?.secPhone?.value;
            }
            if (this?.secPhoneExt?.value !== '') {
                payload.secPhoneExt = this?.secPhoneExt?.value;
            }

            if (this?.cellPhone?.value !== '') {
                payload.cellPhone = this?.cellPhone?.value;
            }
            if (this?.birthDate?.value) {
                payload.birthDate = formatDateOnly(this.birthDate.value);
            }

            if (this?.address?.value !== '') {
                payload.addresses = [this?.address?.value];
            }

            if (this?.picture?.value !== '') {
                payload.picture = this?.picture?.value;
            }
            if (this.changeEmail) {
                payload.email = this?.email?.value ?? '';
                this.pendingEmailChange = { oldEmail: this.originalEmail, newEmail: this?.email?.value ?? null };
            } else {
                this.pendingEmailChange = null;
                payload.email = this.userRecord?.email;
            }
            this.store.dispatch(UpdateUserRecordAction({ payload }));
        }
    }

    private syncEmailWithZitadel(newEmail: string | null): void {
        const oldEmail = this.pendingEmailChange?.oldEmail ?? null;
        const orgId = environment.OIDC_ORG_ID;
        this.userService.updateZitadelEmail(oldEmail, newEmail, orgId, this.userid).subscribe(
            () => {
                this.loading = false;
                this.profileForm.markAsUntouched();
                this.profileForm.markAsPristine();
                this.changeEmail = false;
                this.pendingEmailChange = null;
                setTimeout(() => {
                    this.messageService.success(this?.t?.updateUserRecordSuccess);
                }, 0);
            },
            () => {
                // Zitadel update failed — rollback profile email directly (bypasses store to avoid triggering success toast)
                this.userService
                    .updateUserRecordById(this.userid, { email: oldEmail })
                    .pipe(take(1))
                    .subscribe({
                        complete: () => this.store.dispatch(FetchUserRecordAction({ userid: this.userid })),
                    });
                this.loading = false;
                this.changeEmail = false;
                this.pendingEmailChange = null;
                this.messageService.error(this.translateService.instant('frontEnd.common.errors.unknown'));
            },
        );
    }

    /**
     * Unset the value of the control if it is empty. This is to avoid sending empty strings to the backend.
     * @param control
     * @param fallback - Defaults to undefined
     * @returns
     */
    private getValue<V = string, F = any>(control: AbstractControl, propName: string, fallback: F = undefined): V | F {
        if (control) {
            return control?.value !== '' ? control.value : fallback;
        }
        // if control is hidden
        const value = propName === 'addresses' ? this.userRecord.addresses?.[0] : this.userRecord[propName];
        return value ?? fallback;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    familyEmailCheck(): boolean {
        if (this.activeLinks.length != 0) {
            const hasEmailLinks = this.activeLinks.some((link) => link.userId.email && link.userId.email.trim() !== '');
            return hasEmailLinks;
        }
        return false;
    }

    updateConfirmEmailValidators(email: string) {
        [this.email, this.confirmEmail].forEach((c) => c?.setErrors(null));
        if (email !== this.originalEmail) {
            this.changeEmail = true;
            if (!email) {
                this.confirmEmail?.clearValidators();
                this.confirmEmail?.reset();
            } else {
                this.confirmEmail?.setValidators([Validators.required, Validators.pattern(emailRegex), Validators.minLength(3)]);
                if (this.familyEmailCheck()) {
                    this.confirmEmail?.removeValidators(Validators.required);
                }
            }
        } else {
            this.confirmEmail?.clearValidators();
            this.confirmEmail?.reset();
            this.changeEmail = false;
        }
        this.confirmEmail?.updateValueAndValidity();
    }
}
